import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AthleteProfileShell } from '../components/ProfileShell';
import CampPhotos from '../components/CampPhotos';
import Button from '../components/Button';

export default function MediaPage() {
  const { user, API_URL } = useAuth();
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  const [profileUrl, setProfileUrl] = useState('');
  const [previewModal, setPreviewModal] = useState(null);

  const [profile, setProfile] = useState({
    player_photo: null,
    player_photo_2: null,
    player_photo_3: null,
  });

  const getAxiosConfig = () => ({
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
  });

  const showMessage = (text, type = 'success') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(''), 4000);
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') setPreviewModal(null); };
    if (previewModal) document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [previewModal]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/athletes/profile`, getAxiosConfig());
      if (response.data) {
        setProfile(prev => ({ ...prev, ...response.data }));
        if (response.data.profile_url) setProfileUrl(response.data.profile_url);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const handlePhotoUpload = async (slot) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const formData = new FormData();
      formData.append('file', file);
      formData.append('photo_type', 'player');
      formData.append('slot', slot);

      try {
        await axios.post(`${API_URL}/athletes/upload-photo`, formData, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        showMessage('Photo uploaded');
        fetchProfile();
      } catch (error) {
        showMessage(error.response?.data?.detail || 'Error uploading photo', 'error');
      }
    };
    input.click();
  };

  const handleDeletePhoto = async (slot) => {
    try {
      await axios.delete(`${API_URL}/athletes/delete-photo`, {
        ...getAxiosConfig(),
        data: { slot }
      });
      showMessage('Photo removed');
      fetchProfile();
    } catch (error) {
      showMessage('Error removing photo', 'error');
    }
  };

  const photoSlots = [
    { slot: 1, field: 'player_photo' },
    { slot: 2, field: 'player_photo_2' },
    { slot: 3, field: 'player_photo_3' },
  ];

  const PhotoCard = ({ slot, field, isPrimary }) => {
    const photo = profile[field];
    return (
      <div
        onClick={() => handlePhotoUpload(slot)}
        style={{
          position: 'relative',
          aspectRatio: '3 / 4',
          borderRadius: '16px',
          overflow: 'hidden',
          cursor: 'pointer',
          backgroundColor: '#f1f5f9',
          border: photo ? 'none' : '2px dashed #cbd5e1',
          transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.02)'; e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.12)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = 'none'; }}
        role="button"
        tabIndex={0}
        aria-label={photo ? `Replace photo ${slot}` : `Upload photo ${slot}`}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handlePhotoUpload(slot); } }}
      >
        {photo ? (
          <>
            <img
              src={photo}
              alt={isPrimary ? 'Primary photo' : `Photo ${slot}`}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            {/* Delete button */}
            <Button
              onClick={(e) => { e.stopPropagation(); handleDeletePhoto(slot); }}
              aria-label="Remove photo"
              variant="danger"
              iconOnly
              style={{ position: 'absolute', top: '8px', right: '8px' }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(220,38,38,0.8)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.5)'}
            >
              &times;
            </Button>
          </>
        ) : (
          <div style={{
            width: '100%', height: '100%',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            color: '#94a3b8', gap: '8px',
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </div>
        )}
        {/* Primary badge */}
        {isPrimary && (
          <div style={{
            position: 'absolute', bottom: '8px', left: '8px',
            background: photo ? 'rgba(0,0,0,0.55)' : 'rgba(100,116,139,0.7)',
            backdropFilter: 'blur(4px)',
            color: '#fff', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
            padding: '4px 10px', borderRadius: '20px',
          }}>
            Primary
          </div>
        )}
      </div>
    );
  };

  return (
    <AthleteProfileShell activeTab="Photos">
      {message && (
        <div className={`settings-toast ${messageType === 'error' ? 'settings-toast--error' : ''}`} role="alert">
          {message}
        </div>
      )}

      {/* Photo Grid — dating app style */}
      <div className="settings-section">
        <p className="settings-section__desc">Tap to add or replace. Your first photo is shown in search results.</p>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '12px',
          maxWidth: '480px',
        }}>
          <PhotoCard slot={1} field="player_photo" isPrimary />
          <PhotoCard slot={2} field="player_photo_2" isPrimary={false} />
          <PhotoCard slot={3} field="player_photo_3" isPrimary={false} />
        </div>
        {/* The profile's own user_id, not the logged-in user's: a guardian
            acting on an athlete is a different account. */}
        <CampPhotos userId={profile.user_id} />
      </div>

      {previewModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={() => setPreviewModal(null)}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'relative', width: '90vw', height: '90vh', backgroundColor: '#fff', borderRadius: '12px', overflow: 'hidden' }}
            onClick={(e) => e.stopPropagation()}>
            <Button onClick={() => setPreviewModal(null)} aria-label="Close preview" variant="secondary" iconOnly
              style={{ position: 'absolute', top: '12px', right: '12px', zIndex: 10 }}>
              &times;
            </Button>
            <iframe
              src={previewModal === 'card' ? `/athlete/${user?.uid || '0'}/${profileUrl}/card` : `/athlete/${user?.uid || '0'}/${profileUrl}`}
              title={previewModal === 'card' ? 'Player Card Preview' : 'Public Profile Preview'}
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          </div>
        </div>
      )}
    </AthleteProfileShell>
  );
}
